import { Injectable } from '@angular/core';
import { ModalOptions, ModalService, SimpleModalOptions } from '@healthcatalyst/cashmere';
import { Observable, catchError, map, of, switchMap, take, tap } from 'rxjs';
import { TenantScheduleModalComponent } from './tenant-schedule-modal.component';
import { TenantScheduleVM } from '../models/tenant-schedule-vm.model';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { TenantIngestionJobService } from 'src/app/core/services/application/tenant-ingestion-job.service';
import {
    ScheduleJobsModalComponent,
    ScheduleJobsModalResponse
} from '../../sources/entities/modals/schedule-jobs-modal/schedule-jobs-modal.component';
import { TenantScheduleSummary } from 'src/app/shared/models/tenant-schedule-summary';
import * as tooltips from 'src/app/shared/constants/tooltips';
import { pluralize } from 'src/app/shared/utils/string.utils';

@Injectable({
    providedIn: 'root'
})
export class TenantScheduleModalService {
    tooltips = tooltips;
    constructor(
        private modalService: ModalService,
        private notificationService: ToastNotificationService,
        private tenantIngestionJobService: TenantIngestionJobService
    ) {}

    open(tenantId: string, schedule?: TenantScheduleVM): Observable<any> {
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'lg',
            autoFocus: true,
            data: {
                tenantId: tenantId,
                schedule: schedule
            }
        };
        const modalRef = this.modalService.open(TenantScheduleModalComponent, options);
        return modalRef.result;
    }

    activateJobs(tenantId: string, ingestionIds: number[]): Observable<boolean> {
        const simpleOptions: SimpleModalOptions = {
            icon: 'icon-toggle-on',
            headerText: 'Activate Jobs?',
            message: 'Are you sure you want to activate the selected job(s)?',
            confirmButtonText: 'Activate'
        };
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'md'
        };
        return this.modalService.confirm(simpleOptions, options).pipe(
            take(1),
            switchMap((result: boolean) => {
                if (!result) {
                    return of(false);
                }

                return this.tenantIngestionJobService.activateTenantJobs(tenantId, ingestionIds).pipe(
                    tap(() =>
                        this.notificationService.showSuccess(
                            `Successfully activated ${ingestionIds.length} ${pluralize('job', ingestionIds.length)}.`,
                            `Activated ${pluralize('Job', ingestionIds.length)}`
                        )
                    ),
                    map(() => true),
                    catchError(() => {
                        this.notificationService.showError(
                            'An unexpected error occurred while attempting to activate the selected job(s). Please contact an admin if the problem continues.',
                            'Update Failed'
                        );
                        return of(false);
                    })
                );
            })
        );
    }

    deactivateJobs(tenantId: string, ingestionIds: number[]): Observable<boolean> {
        const simpleOptions: SimpleModalOptions = {
            icon: 'icon-toggle-off',
            headerText: 'Deactivate Jobs?',
            message: 'Are you sure you want to deactivate the selected job(s)?',
            confirmButtonText: 'Deactivate'
        };
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'md'
        };
        return this.modalService.confirmDestructive(simpleOptions, options).pipe(
            take(1),
            switchMap((result: boolean) => {
                if (!result) {
                    return of(false);
                }

                return this.tenantIngestionJobService.deactivateTenantJobs(tenantId, ingestionIds).pipe(
                    tap(() =>
                        this.notificationService.showSuccess(
                            `Successfully deactivated ${ingestionIds.length} ${pluralize('job', ingestionIds.length)}.`,
                            `Deactivated ${pluralize('Job', ingestionIds.length)}`
                        )
                    ),
                    map(() => true),
                    catchError(() => {
                        this.notificationService.showError(
                            'An unexpected error occurred while attempting to deactivate the selected job(s). ' +
                                'Please contact an admin if the problem continues.',
                            'Update Failed'
                        );
                        return of(false);
                    })
                );
            })
        );
    }

    openConfirmDeactivateSchedule(activeIngestionJobCount: number) {
        const simpleOptions: SimpleModalOptions = {
            headerText: 'Deactivate Schedule?',
            icon: 'icon-warn',
            confirmButtonText: 'Deactivate Schedule',
            messageHTML: `This schedule is still used by <strong>${activeIngestionJobCount}</strong> entities with active status. Are you sure you want to deactivate this schedule?`
        };
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'md'
        };

        this.modalService.allowMultiple = true;
        return this.modalService.confirmDestructive(simpleOptions, options);
    }

    openConfirmDeleteSingle(cadence: string) {
        const simpleOptions: SimpleModalOptions = {
            headerText: 'Delete Schedule?',
            icon: 'icon-trash',
            confirmButtonText: 'Delete Schedule',
            confirmButtonIsDestructive: true,
            messageHTML: `Are you sure you want to delete the  <strong>${cadence}</strong> schedule?`,
            footerTooltipText: 'Associated Jobs will no longer be scheduled'
        };
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'md'
        };

        this.modalService.allowMultiple = true;
        return this.modalService.confirmDestructive(simpleOptions, options);
    }

    openConfirmDeleteMultiple(cadences: string[]) {
        const cadenceHTML = cadences.map(cadence => `<li> ${cadence} </li>`).join(' ');
        const simpleOptions: SimpleModalOptions = {
            headerText: `Delete Schedules (${cadences.length})?`,
            icon: 'icon-trash',
            confirmButtonText: `Delete Schedules (${cadences.length})`,
            confirmButtonIsDestructive: true,
            messageHTML: `Are you sure you want to delete these <strong>${cadences.length} schedules</strong>? <ul class='confirm-modal'>${cadenceHTML}</ul>`,
            footerTooltipText: 'Jobs associated with these schedules will no longer be run'
        };
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'md'
        };

        this.modalService.allowMultiple = true;
        return this.modalService.confirmDestructive(simpleOptions, options);
    }

    openScheduleJobs(
        tenantId: string,
        selectedSchedules: number[],
        schedules: TenantScheduleSummary[],
        selectedJobs: number[] = []
    ): Observable<ScheduleJobsModalResponse> {
        const options: ModalOptions = {
            ignoreEscapeKey: true,
            ignoreOverlayClick: true,
            size: 'xl',
            data: { tenantId, selectedSchedules, schedules, selectedJobs }
        };

        const modalRef = this.modalService.open(ScheduleJobsModalComponent, options);
        return <Observable<ScheduleJobsModalResponse>>modalRef.result;
    }
}
